import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  Column,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiLink,
  MuiNotesToolTip,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  RowValue,
} from "../../../../../../components";
import { AmountWithSign } from "../../../../../../components/amountWithSign/AmountWithSign";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import {
  INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getAmountWithCurrency,
  getFullName,
  getValueOrBlank,
  isStatusClosed,
  isStatusInvoiced,
  isValueIncludedIgnoreCase,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { programStatusSelector } from "../../../../../../redux/slices/program/programSelector";
import { Currency } from "../../../../../createClient/modelsAndSchemas/Models";
import {
  InvoiceHistoryColumnKey,
  ProgramInvoice,
  ProgramInvoiceItem,
} from "../../../../modelsAndSchemas/Models";
import { invoiceHistorySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.PROGRAM_TEMPLATE}-invoice-history-list`;

interface InvoiceHistoryListProps {
  onReverseClick: (selectedInvoice: ProgramInvoice) => void;
  onViewInvoiceClick: (selectedInvoice: ProgramInvoice) => void;
  onManageRevenueSplitClick: (selectedInvoice: ProgramInvoiceItem) => void;
  onExportInvoiceClick: (selectedInvoice: ProgramInvoice) => void;
  onDownloadInvoiceClick: (selectedInvoice: ProgramInvoice) => void;
  data: ProgramInvoiceItem[] | undefined; // Accept invoice data from parent, type as needed
  isViewOnly?: boolean;
  currency: Currency;
}
export const InvoiceHistoryList = ({
  onReverseClick,
  onViewInvoiceClick,
  onManageRevenueSplitClick,
  onExportInvoiceClick,
  onDownloadInvoiceClick,
  data,
  isViewOnly,
  currency,
}: InvoiceHistoryListProps) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const getLabel = (key: keyof typeof invoiceHistorySiteCoreKeys) => {
    return fetchLabel(
      invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      invoiceHistorySiteCoreKeys[key],
    );
  };

  const { control } = useForm<Partial<ProgramInvoice>>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const currencyList = useSelector(currencyListSelector);
  const [state, setState] = useState<{
    filteredList: ProgramInvoiceItem[];
    originalList: ProgramInvoiceItem[];
  }>({
    filteredList: [],
    originalList: [],
  });
  const programStatusId = useSelector(programStatusSelector);
  const isProgramStatusNotClosed = useMemo(
    () => !isStatusClosed(programStatusId as StatusBadgeType),
    [programStatusId],
  );

  useEffect(() => {
    if (data?.length) {
      setState((prevState) => ({
        ...prevState,
        filteredList: data,
        originalList: data,
      }));
      tableRef.current?.resetPagination();
    }
  }, [data]);

  const handleOnViewClick = (selectedInvoice: ProgramInvoice) => {
    onViewInvoiceClick(selectedInvoice);
  };

  const handleOnReverseClick = (selectedInvoice: ProgramInvoice) => {
    onReverseClick(selectedInvoice);
  };

  const handleManageRevenueSplitClick = (selectedInvoice: ProgramInvoice) => {
    onManageRevenueSplitClick(selectedInvoice as ProgramInvoiceItem);
  };
  const handleOnExportInvoiceClick = (selectedInvoice: ProgramInvoice) => {
    onExportInvoiceClick(selectedInvoice);
  };
  const handleOnDownloadInvoiceClick = (selectedInvoice: ProgramInvoice) => {
    onDownloadInvoiceClick(selectedInvoice);
  };

  const filters = useWatch({ control });

  useEffect(() => {
    appliedFilters(state.originalList);
  }, [filters, state.originalList]);

  const appliedFilters = (invoiceData: ProgramInvoiceItem[] = []) => {
    if (!invoiceData.length) return;

    let filteredList = invoiceData.map((item) => ({
      ...item,
      reversalReason:
        item?.reversalReason !== INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY
          ? fetchValueByKeyOfDropdown(
              invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
              invoiceHistorySiteCoreKeys.confirmationPopUpInvoiceReversalReasonList,
              item?.reversalReason ?? "",
            )
          : getValueOrBlank(item?.reversalComments),
    }));

    const {
      number,
      date,
      statusId,
      totalInvoiceAmount,
      reversalReason,
      issuedBy,
      purchaseOrder,
    } = filters || {};

    filteredList = filteredList.filter((item) => {
      const issuedByMatches =
        isValueIncludedIgnoreCase(item.issuedBy?.firstName, issuedBy) ||
        isValueIncludedIgnoreCase(item.issuedBy?.lastName, issuedBy) ||
        isValueIncludedIgnoreCase(item.issuedBy?.email, issuedBy);
      return (
        isValueIncludedIgnoreCase(item.number, number) &&
        isValueIncludedIgnoreCase(
          convertToUserFormat(item.date, userCountryDateFormat),
          date,
        ) &&
        isValueIncludedIgnoreCase(item.statusId, statusId) &&
        isValueIncludedIgnoreCase(item.reversalReason, reversalReason) &&
        isValueIncludedIgnoreCase(
          item.totalInvoiceAmount,
          totalInvoiceAmount,
        ) &&
        issuedByMatches &&
        isValueIncludedIgnoreCase(item.purchaseOrder?.number, purchaseOrder)
      );
    });

    setState((prevState: any) => ({
      ...prevState,
      filteredList,
    }));
    tableRef.current?.resetPagination();
  };

  const columns: Column[] = [
    {
      id: InvoiceHistoryColumnKey.number,
      label: getLabel("invoiceNumberTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) =>
        hasModulePermission(MODULE_PERMISSIONS.PROGRAM_VIEW_INVOICE) ? (
          <MuiLink
            dataTestId="invoice-number-link"
            onClick={() => handleOnViewClick(row as unknown as ProgramInvoice)}
          >
            <MuiBox>
              <MuiTypography variant="h3" className="Link">
                {row[InvoiceHistoryColumnKey.number]}
              </MuiTypography>
            </MuiBox>
          </MuiLink>
        ) : (
          <MuiBox>
            <MuiTypography variant="h3" className="">
              {row[InvoiceHistoryColumnKey.number]}
            </MuiTypography>
          </MuiBox>
        ),
    },
    {
      id: InvoiceHistoryColumnKey.date,
      label: getLabel("invoicedDateTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {row?.date
              ? convertToUserFormat(row.date, userCountryDateFormat)
              : ""}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: InvoiceHistoryColumnKey.statusId,
      label: getLabel("invoiceStatusTableColumn"),
      isSortVisible: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <StatusBadge
          status={row.statusId}
          label={fetchValueByKeyOfDropdown(
            invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
            invoiceHistorySiteCoreKeys.invoiceStatusList,
            row.statusId,
          )}
        />
      ),
    },
    {
      id: InvoiceHistoryColumnKey.reversalReason,
      label: getLabel("reversalReasonTableColumn"),
      isSortVisible: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <MuiNotesToolTip message={row?.reversalReason} />
      ),
    },
    {
      id: InvoiceHistoryColumnKey.totalInvoiceAmount,
      label: getLabel("totalBillingAmountTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => {
        const amtWithCurrency = getAmountWithCurrency(
          row.totalInvoiceAmount ?? 0,
          getValueOrBlank(currency?.name),
        );

        const sign = isStatusInvoiced(row.statusId) ? "+" : "-";
        return <AmountWithSign currency={amtWithCurrency} sign={sign} />;
      },
    },
    {
      id: InvoiceHistoryColumnKey.issuedBy,
      label: getLabel("issuedByTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => {
        const issuedBy = row.issuedBy;
        return (
          <MuiBox>
            <MuiTypography variant="h3" className="">
              {getFullName(issuedBy?.firstName, issuedBy?.lastName, true)}
            </MuiTypography>
          </MuiBox>
        );
      },
    },
    {
      id: InvoiceHistoryColumnKey.purchaseOrder,
      label: getLabel("poNumberTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => {
        const purchaseOrder = row.purchaseOrder;
        return (
          <MuiBox>
            <MuiTypography variant="h3" className="">
              {purchaseOrder?.number}
            </MuiTypography>
          </MuiBox>
        );
      },
    },
    ...(hasModulePermission(MODULE_PERMISSIONS.PROGRAM_INVOICE_REVERSAL)
      ? [
          {
            id: "action",
            label: getLabel("actionTableColumn"),
            render: (row: RowProps) => {
              let actions = [];
              if (
                isProgramStatusNotClosed && !isViewOnly && 
                !(
                  row?.isInvoiceReversed ||
                  row?.statusId === STATUS_BADGE_TYPE.reversed
                )
              ) {
                actions.push({
                  onClick: () => handleOnReverseClick(row),
                  dataTestId: "reverse-invoice",
                  label: getLabel("reverseInvoice"),
                });
              }

              if (row?.hasRevenueSplits) {
                actions.push({
                  onClick: () => handleManageRevenueSplitClick(row),
                  dataTestId: isViewOnly
                    ? "view-revenue-split"
                    : "manage-revenue-split",
                  label: fetchLabel(
                    invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
                    isViewOnly
                      ? invoiceHistorySiteCoreKeys.viewRevenueSplitAction
                      : invoiceHistorySiteCoreKeys.manageRevenueSplitAction,
                  ),
                });
              }

              actions.push({
                onClick: () => handleOnExportInvoiceClick(row),
                dataTestId: "export-invoice",
                label: commonLabels.exportToExcel,
              });

                actions.push({
                onClick: () => handleOnDownloadInvoiceClick(row),
                dataTestId: "download-invoice",
                label: commonLabels.downloadInvoice
              });

              return <MuiOptionMenu menuItems={actions} />;
            },
          },
        ]
      : []),
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm: React.JSX.Element[] = [
    <FormController
      control={control}
      name={InvoiceHistoryColumnKey.number}
      key={InvoiceHistoryColumnKey.number}
    >
      <MuiTextField
        id={`${pageId}-${InvoiceHistoryColumnKey.number}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      control={control}
      clearable
      name={InvoiceHistoryColumnKey.date}
      key={InvoiceHistoryColumnKey.date}
      format={userCountryDateFormat}
      valueFormat={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,

    <FormController
      control={control}
      name={InvoiceHistoryColumnKey.statusId}
      key={InvoiceHistoryColumnKey.statusId}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(
          invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
          invoiceHistorySiteCoreKeys.invoiceStatusList,
        )}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${InvoiceHistoryColumnKey.statusId}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={InvoiceHistoryColumnKey.reversalReason}
      key={InvoiceHistoryColumnKey.reversalReason}
    >
      <MuiTextField
        id={`${pageId}-${InvoiceHistoryColumnKey.reversalReason}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={InvoiceHistoryColumnKey.totalInvoiceAmount}
      key={InvoiceHistoryColumnKey.totalInvoiceAmount}
    >
      <MuiTextField
        id={`${pageId}-${InvoiceHistoryColumnKey.totalInvoiceAmount}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={InvoiceHistoryColumnKey.issuedBy}
      key={InvoiceHistoryColumnKey.issuedBy}
    >
      <MuiTextField
        id={`${pageId}-${InvoiceHistoryColumnKey.issuedBy}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={InvoiceHistoryColumnKey.purchaseOrder}
      key={InvoiceHistoryColumnKey.purchaseOrder}
    >
      <MuiTextField
        id={`${pageId}-${InvoiceHistoryColumnKey.purchaseOrder}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <></>,
  ];

  return (
    <MuiTable
      ref={tableRef}
      pageId={pageId}
      isLastColumnSticky={hasModulePermission(
        MODULE_PERMISSIONS.PROGRAM_INVOICE_REVERSAL,
      )}
      tableHeaderForm={tableHeaderForm}
      columns={columns}
      tableResults={state.filteredList as unknown as Record<string, RowValue>[]}
      noDataInApi={state.originalList.length === 0}
      noResultMessage={commonLabels.searchCriteria}
      priorityResults={[]}
    />
  );
};
